const cache = new Map();
const queues = new Map();

/**
 * Fetches the resource and blocks subsequent requests for that url until the first one is done in order to prevent duplicate requests
 *
 * @param {string} url - The resource URL to fetch.
 * @param {Object} [options] - Optional settings.
 * @param {boolean} [options.useCache=false] - When false, use relies on the browser's caching to avoid duplicate requests based on cache headers. When true, uses an internal cache to return the original response.
 * @returns {Promise<Response>} A promise that resolves to the fetched Response object.
 */
export async function fetchResource(url, { useCache = false } = {}) {
  const result = cache.get(url);

  if (!result) {
    // Return a promise that resolves with the response from the first request
    const promise = fetch(url);
    promise.then((response) => {
      cache.set(url, prepareResponse(response)); // Store the response instead of the promise so we know this one is complete
      processQueue(url);
    });
    cache.set(url, promise);

    return promise;
  } else if (useCache && result instanceof Promise) {
    // The first request is not complete so return the first request's promise
    return result.then(() => prepareResponse(cache.get(url)));
  } else if (useCache) {
    // The first request is complete and we want to use the cache so return a promise the resolves with first request's response
    return Promise.resolve(prepareResponse(result));
  } else if (result instanceof Promise) {
    // The first request is not complete so queue a new request for after the first request completes
    return enqueue(url);
  } // The first request is complete so immediately fetch the url
  return fetch(url);
}

function enqueue(url) {
  let queue = queues.get(url);

  if (!queue) {
    queue = [];
    queues.set(url, queue);
  }

  return new Promise((resolve, reject) => {
    queue.push({ resolve, reject });
  });
}

function processQueue(url) {
  const queue = queues.get(url);
  if (queue) {
    queue.forEach((queued) => {
      fetch(url).then((response) => queued.resolve(prepareResponse(response)));
    });
    queues.delete(url); // Empty the queue
  }
}

// Clone the response because as soon as it is read it cannot be read again
function prepareResponse(response) {
  return response.clone();
}
